import { useState } from 'react';
import { CalendarClock, FileText } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { formatMoney } from '@/client/types/payment';
import {
  useStoreInvoiceQuery,
  useStoreInvoicesQuery,
  useStoreRenewalQuery,
} from '@/client/api/stores/parity.query';
import { useCancelStoreRenewal, useResumeStoreRenewal } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { RelationshipLink } from '@/client/components/admin/Payment/RelationshipLink';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { InfoRow, Pager, Pill, SectionCard, label, shortDate, statusTone, InfoRowsSkeleton, ListSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';

const LIMIT = 20;
const INVOICE_STATUSES = ['paid', 'unpaid', 'pending', 'overdue', 'canceled', 'voided', 'refunded'];
const BILLING_TYPES = ['plan_renewal', 'order', 'one_time', 'recurring'];

const InvoiceDialog: React.FC<{ storeid: number; invid: number; onClose: () => void }> = ({ storeid, invid, onClose }) => {
  const { data: inv, isLoading } = useStoreInvoiceQuery(storeid, invid);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invoice {inv?.ffid ?? `#${invid}`}</DialogTitle>
          <DialogDescription>{inv ? `${label(inv.billing_type)} · ${label(inv.status)}` : 'Loading…'}</DialogDescription>
        </DialogHeader>
        {isLoading || !inv ? (
          <InfoRowsSkeleton rows={6} />
        ) : (
          <div className="space-y-3">
            <div className="divide-y divide-gray-100">
              <InfoRow label="Amount">{formatMoney(inv.amount ?? 0, inv.ccode ?? 'GHS')}</InfoRow>
              <InfoRow label="Issued">{shortDate(inv.date_added)}</InfoRow>
              <InfoRow label="Due">{shortDate(inv.date_due)}</InfoRow>
              <InfoRow label="Paid">{shortDate(inv.date_paid)}</InfoRow>
              <InfoRow label="In Payments"><RelationshipLink kind="invoice" id={inv.invid} /></InfoRow>
            </div>
            {inv.payments && inv.payments.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Payments</p>
                {inv.payments.map((p) => (
                  <div key={p.pid} className="flex items-center justify-between py-1 text-sm">
                    <RelationshipLink kind="payment" id={p.pid} />
                    <span className="tabular-nums">{formatMoney(p.amount, p.ccode ?? 'GHS')}</span>
                    <span className="text-xs text-gray-500">
                      {p.refund ? 'refunded' : p.canceled ? 'canceled' : p.fulfilled ? 'fulfilled' : 'pending'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * The store's plan renewal and invoices (`admin.store.billing`). Staff can
 * cancel a pending renewal (which voids its unpaid invoice) or resume it; they
 * never pay on the seller's behalf.
 */
export const BillingTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [status, setStatus] = useState('');
  const [billingType, setBillingType] = useState('');
  const [offset, setOffset] = useState(0);
  const [openInvoice, setOpenInvoice] = useState<number | null>(null);
  const [dialog, setDialog] = useState<null | 'cancel' | 'resume'>(null);

  const { data: renewalData, isLoading: loadingRenewal } = useStoreRenewalQuery(store.storeid);
  const { data: invoices, isLoading } = useStoreInvoicesQuery(store.storeid, {
    status: status || undefined, billing_type: billingType || undefined, limit: LIMIT, offset,
  });
  const cancel = useCancelStoreRenewal();
  const resume = useResumeStoreRenewal();
  const renewal = renewalData?.renewal ?? null;

  return (
    <div className="space-y-4">
      <SectionCard
        title="Plan renewal"
        description="The next charge for the store's plan."
        actions={
          <div className="flex gap-2">
            {renewal && (
              <PermissionButton code={PERMISSIONS.store.billing} size="sm" variant="destructive" onClick={() => setDialog('cancel')}>
                Cancel renewal
              </PermissionButton>
            )}
            <PermissionButton code={PERMISSIONS.store.billing} size="sm" variant="outline" onClick={() => setDialog('resume')}>
              Resume renewal
            </PermissionButton>
          </div>
        }
      >
        {loadingRenewal ? (
          <div className="grid gap-x-8 sm:grid-cols-2"><InfoRowsSkeleton rows={3} /><InfoRowsSkeleton rows={3} /></div>
        ) : !renewal ? (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <CalendarClock className="h-4 w-4" /> No pending renewal. If the seller cancelled at period end, “Resume” turns renewal back on.
          </p>
        ) : (
          <div className="grid gap-x-8 sm:grid-cols-2">
            <InfoRow label="Plan">{renewal.plan_name ?? '—'}{renewal.plan_tier ? ` (${renewal.plan_tier})` : ''}</InfoRow>
            <InfoRow label="Amount">{renewal.amount != null ? formatMoney(renewal.amount, renewal.ccode ?? 'GHS') : '—'}</InfoRow>
            <InfoRow label="Due">{shortDate(renewal.date_due)}</InfoRow>
            <InfoRow label="Grace ends">{shortDate(renewal.date_grace_end)}</InfoRow>
            <InfoRow label="State">
              <Pill tone={renewal.is_overdue ? 'bg-red-100 text-red-800' : renewal.is_in_grace ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                {renewal.is_overdue ? 'overdue' : renewal.is_in_grace ? 'in grace' : label(renewal.status)}
              </Pill>
            </InfoRow>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Invoices"
        actions={
          <div className="flex gap-2">
            <select value={billingType} onChange={(e) => { setBillingType(e.target.value); setOffset(0); }} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" aria-label="Type">
              <option value="">All types</option>
              {BILLING_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" aria-label="Status">
              <option value="">All statuses</option>
              {INVOICE_STATUSES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </div>
        }
      >
        {isLoading ? (
          <ListSkeleton rows={5} />
        ) : !invoices?.invoices.length ? (
          <p className="py-6 text-center text-sm text-gray-400">No invoices.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {invoices.invoices.map((inv) => (
              <button
                key={inv.invid}
                type="button"
                onClick={() => setOpenInvoice(inv.invid)}
                className="flex w-full items-center gap-3 py-2.5 text-left text-sm hover:bg-gray-50"
              >
                <FileText className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-gray-900">{inv.ffid ?? `#${inv.invid}`}</span>
                  <span className="ml-2 text-xs text-gray-500">{label(inv.billing_type)} · {shortDate(inv.date_added)}</span>
                </span>
                <span className="tabular-nums">{formatMoney(inv.amount ?? 0, inv.ccode ?? 'GHS')}</span>
                <Pill tone={statusTone(inv.status)}>{label(inv.status)}</Pill>
              </button>
            ))}
          </div>
        )}
        <Pager total={invoices?.total_count ?? 0} limit={LIMIT} offset={offset} onChange={setOffset} />
      </SectionCard>

      {openInvoice !== null && <InvoiceDialog storeid={store.storeid} invid={openInvoice} onClose={() => setOpenInvoice(null)} />}

      {dialog && (
        <ReasonDialog
          open
          title={dialog === 'cancel' ? 'Cancel plan renewal' : 'Resume plan renewal'}
          description={
            dialog === 'cancel'
              ? 'The plan runs to the end of its term and is not renewed. The pending renewal invoice, if unpaid, is voided.'
              : 'Turns renewal back on (and reactivates a plan still within its term).'
          }
          confirmLabel={dialog === 'cancel' ? 'Cancel renewal' : 'Resume'}
          destructive={dialog === 'cancel'}
          reasonRequired
          loading={cancel.isPending || resume.isPending}
          onConfirm={(reason) =>
            (dialog === 'cancel' ? cancel : resume).mutate(
              { storeid: store.storeid, reason },
              { onSuccess: () => setDialog(null) },
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};
